(() => {
  if (window.EdenTpFulfillment) {
    window.EdenTpFulfillment.refresh();
    return;
  }

  const formSelector = 'form[data-tp-checkout-form]';
  const methodSelector = 'input[name="attributes[TP Fulfillment Method]"]';
  const shippingServiceSelector = 'input[name="attributes[TP Shipping Service]"]';
  const deliveryDateSelector = '[name="attributes[TP Requested Delivery Date]"]';
  const deliveryWindowSelector = '[name="attributes[TP Requested Delivery Window]"]';
  const courierTipSelector = 'input[name="attributes[TP Courier Tip Percent]"]';
  const emailSelector = '[name="contact[email]"]';
  const emailConfirmationSelector = '[name="contact[email_confirmation]"]';
  const discountVerificationCodeSelector = '[data-tp-discount-verification-code]';
  const addressFieldNames = [
    'shipping_address[first_name]',
    'shipping_address[last_name]',
    'shipping_address[address1]',
    'shipping_address[address2]',
    'shipping_address[city]',
    'shipping_address[province]',
    'shipping_address[zip]',
    'shipping_address[country_code]',
    'contact[phone]',
  ];
  const checkoutValidationSelector = [
    emailSelector,
    emailConfirmationSelector,
    ...addressFieldNames.map((name) => `[name="${name}"]`),
  ].join(', ');
  const shippingServices = new Set(['priority', 'priority_express']);
  const persistedFieldSelector = [
    methodSelector,
    shippingServiceSelector,
    deliveryDateSelector,
    deliveryWindowSelector,
    courierTipSelector,
    '[name="attributes[Referral]"]',
    '[name="attributes[Special Delivery Instructions]"]',
  ].join(', ');
  const localDeliveryMinimumCents = 10000;
  const localDeliveryCurrency = 'USD';
  const fulfillmentContractVersion = 1;
  const deliveryWindowContractVersion = 1;
  const deliveryWindowTimezone = 'America/New_York';
  const courierTipContractVersion = 2;
  const legacyCourierTipContractVersion = 1;
  const courierTipPercentChoices = new Set([0, 10, 15, 20]);
  const legacyCourierTipPercentChoices = new Set([0, 15, 20, 25]);
  const defaultCourierTipPercent = 15;
  const localDeliveryMinimumMessage =
    'Free local delivery requires a discounted cart subtotal of at least $100.00. Add items or choose shipping.';
  const localDeliveryVerificationMessage =
    'We could not verify the discounted cart subtotal for local delivery. Refresh the cart or choose shipping.';
  const persistenceErrorMessage = 'We could not save your delivery choice. Please try again.';
  const orderSubmissionErrorMessage = 'We could not complete your order. Please try again.';
  const discountVerificationMaxAttempts = 5;
  const cartRequestTimeoutMs = 10000;
  const timers = new Map();
  const pendingSubmissions = new WeakSet();
  const canonicalTextAttributes = new Map();
  const confirmationStates = new WeakMap();
  let tpId = '';
  let cartToken = '';
  let cartItemCount = null;
  let selectedMethod = '';
  let selectedShippingService = '';
  let requestedDeliveryDate = '';
  let requestedDeliveryWindow = '';
  let deliveryWindowState = { status: 'idle', dates: new Map() };
  let courierTipCapabilityEnabled = false;
  let courierTipCapabilityReady = false;
  let selectedCourierTipPercent = null;
  let deliveryWindowRequest = null;
  let forcedDeliveryWindowRequest = null;
  let tpIdPersistenceStarted = false;
  let checkoutInProgress = false;
  let refreshFrame = null;
  let cartRefreshInFlight = null;
  let cartRefreshIncludesCourierTipCapability = false;
  let queuedCourierTipCartRefresh = null;
  let fulfillmentIntentRevision = 0;
  let cartPricing = { subtotalCents: null, currency: '' };
  let persistenceQueue = Promise.resolve({ ok: true });
  let exhaustedUnverifiedAddressFingerprint = '';
  const discountVerification = {
    status: 'idle',
    revision: 0,
    challengeId: '',
    emailMasked: '',
    expiresAt: '',
    message: '',
    errorMessage: '',
    attempts: 0,
    proofToken: '',
    proofExpiresAt: '',
    binding: null,
  };

  const formControls = (form, selector) =>
    [...(form?.elements || [])].filter(
      (control) => control instanceof Element && control.matches(selector)
    );

  const formControl = (form, selector) => formControls(form, selector)[0] || null;

  const methodFieldset = (form) => formControl(form, '[data-delivery-method]');

  const deliveryWindowFieldset = (form) =>
    formControl(form, '[data-delivery-window-options]');

  const courierTipFieldset = (form) =>
    formControl(form, '[data-courier-tip-options]');

  const generateUuid = () => {
    if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
      throw new Error('A cryptographically secure random source is required.');
    }

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return [...bytes]
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('')
      .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
  };

  const currentForm = (formId) => document.getElementById(formId);

  const confirmationState = (form) => {
    if (!confirmationStates.has(form)) {
      confirmationStates.set(form, {
        type: '',
        suggestedEmail: '',
        suggestedAddress: null,
        enteredAddress: null,
        emailDomainAcknowledged: false,
        acknowledgedEmail: '',
        acceptedAddressFingerprint: '',
        pendingUnverifiedAddressFingerprint: '',
        acknowledgedUnverifiedAddressFingerprint: '',
      });
    }
    return confirmationStates.get(form);
  };

  const confirmationPrompt = (form) => {
    const promptId = String(form?.dataset?.tpValidationPrompt || '').trim();
    return promptId ? document.getElementById(promptId) : null;
  };

  const discountVerificationPrompt = (form) => {
    const promptId = String(form?.dataset?.tpDiscountVerificationPrompt || '').trim();
    return promptId ? document.getElementById(promptId) : null;
  };

  const syncDiscountVerificationPrompt = (form) => {
    const prompt = discountVerificationPrompt(form);
    if (!prompt) return;
    const visible = ['challenge', 'verifying', 'invalid', 'locked', 'expired'].includes(
      discountVerification.status
    );
    const message = prompt.querySelector('[data-tp-discount-verification-message]');
    const destination = prompt.querySelector('[data-tp-discount-verification-destination]');
    const code = prompt.querySelector(discountVerificationCodeSelector);
    const verify = prompt.querySelector('[data-tp-verify-discount]');
    const error = prompt.querySelector('[data-tp-discount-verification-error]');

    prompt.hidden = !visible;
    if (message) message.textContent = visible ? discountVerification.message : '';
    if (destination) {
      destination.textContent = visible && discountVerification.emailMasked
        ? `Code sent to ${discountVerification.emailMasked}.`
        : '';
    }
    if (code) {
      code.disabled = discountVerification.status !== 'challenge';
      if (!visible) {
        code.value = '';
        code.setCustomValidity('');
      }
    }
    if (verify) {
      verify.disabled = discountVerification.status !== 'challenge';
      verify.textContent = discountVerification.status === 'verifying'
        ? 'Verifying...'
        : 'Verify email and continue';
    }
    if (error) {
      error.textContent = visible ? discountVerification.errorMessage : '';
      error.hidden = !visible || !discountVerification.errorMessage;
    }
  };

  const resetDiscountVerification = () => {
    discountVerification.status = 'idle';
    discountVerification.revision += 1;
    discountVerification.challengeId = '';
    discountVerification.emailMasked = '';
    discountVerification.expiresAt = '';
    discountVerification.message = '';
    discountVerification.errorMessage = '';
    discountVerification.attempts = 0;
    discountVerification.proofToken = '';
    discountVerification.proofExpiresAt = '';
    discountVerification.binding = null;
    document.querySelectorAll(formSelector).forEach(syncDiscountVerificationPrompt);
  };

  const clearConfirmationPrompt = (form) => {
    const prompt = confirmationPrompt(form);
    if (prompt) {
      const title = prompt.querySelector('[data-tp-confirmation-title]');
      const message = prompt.querySelector('[data-tp-confirmation-message]');
      const suggestion = prompt.querySelector('[data-tp-confirmation-suggestion]');
      const accept = prompt.querySelector('[data-tp-accept-suggestion]');
      const reject = prompt.querySelector('[data-tp-reject-suggestion]');
      if (title) title.textContent = '';
      if (message) message.textContent = '';
      if (suggestion) {
        suggestion.textContent = '';
        suggestion.hidden = true;
      }
      if (accept) accept.textContent = '';
      if (reject) reject.textContent = '';
      prompt.hidden = true;
    }
    const state = confirmationState(form);
    state.type = '';
    state.suggestedEmail = '';
    state.suggestedAddress = null;
    state.enteredAddress = null;
    state.pendingUnverifiedAddressFingerprint = '';
  };

  const showConfirmationPrompt = (form, detail) => {
    const prompt = confirmationPrompt(form);
    if (!prompt) return false;
    const state = confirmationState(form);
    const title = prompt.querySelector('[data-tp-confirmation-title]');
    const message = prompt.querySelector('[data-tp-confirmation-message]');
    const suggestion = prompt.querySelector('[data-tp-confirmation-suggestion]');
    const accept = prompt.querySelector('[data-tp-accept-suggestion]');
    const reject = prompt.querySelector('[data-tp-reject-suggestion]');
    if (!title || !message || !suggestion || !accept || !reject) return false;

    state.type = detail.code;
    state.suggestedEmail = detail.suggestedEmail || '';
    state.suggestedAddress = detail.suggestedAddress || null;
    state.enteredAddress = detail.enteredAddress || null;
    state.pendingUnverifiedAddressFingerprint = detail.addressFingerprint || '';
    title.textContent = detail.code === 'email_domain_suggestion'
      ? 'Confirm your email address'
      : 'Confirm your delivery address';
    message.textContent = detail.message;
    if (detail.code === 'email_domain_suggestion') {
      suggestion.textContent = `Suggested email: ${detail.suggestedEmail}`;
      accept.textContent = 'Use suggested email';
      reject.textContent = 'Keep entered email';
    } else {
      const address = detail.code === 'address_confirmation_required'
        ? detail.enteredAddress
        : detail.suggestedAddress;
      suggestion.textContent = [
        `${address.first_name || ''} ${address.last_name || ''}`.trim(),
        address.address1,
        address.address2,
        `${address.city}, ${address.province} ${address.zip}`,
        address.country_code,
        address.phone,
      ].filter(Boolean).join('\n');
      accept.textContent = detail.code === 'address_confirmation_required'
        ? 'Use this address anyway'
        : 'Use corrected address';
      reject.textContent = 'Edit address';
    }
    suggestion.hidden = false;
    prompt.hidden = false;
    clearPersistenceError(form);
    if (typeof prompt.focus === 'function') prompt.focus();
    return true;
  };

  const showPersistenceError = (form, message = persistenceErrorMessage) => {
    const error = methodFieldset(form)?.querySelector('[data-delivery-persistence-error]');
    if (!error) return null;
    if (error.textContent !== message) error.textContent = message;
    if (error.hidden) error.hidden = false;
    return error;
  };

  const clearPersistenceError = (form) => {
    const error = methodFieldset(form)?.querySelector('[data-delivery-persistence-error]');
    if (!error) return;
    if (error.textContent) error.textContent = '';
    if (!error.hidden) error.hidden = true;
  };

  const setMethodError = (message = '') => {
    document.querySelectorAll(formSelector).forEach((form) => {
      const fieldset = methodFieldset(form);
      const error = fieldset?.querySelector('[data-delivery-method-error]');
      if (!error) return;
      if (error.textContent !== message) error.textContent = message;
      if (error.hidden !== !message) error.hidden = !message;
      if (message) fieldset.setAttribute('aria-invalid', 'true');
      else fieldset.removeAttribute('aria-invalid');
    });
  };

  const setOptionInvalid = (option, invalid) => {
    if (!option) return;
    if (invalid) option.setAttribute('aria-invalid', 'true');
    else option.removeAttribute('aria-invalid');
  };

  const parseCents = (value) => {
    if (typeof value === 'number') {
      return Number.isSafeInteger(value) && value >= 0 ? value : null;
    }
    const normalized = String(value ?? '').trim();
    if (!/^\d+$/.test(normalized)) return null;
    const parsed = Number(normalized);
    return Number.isSafeInteger(parsed) ? parsed : null;
  };

  const normalizeCurrency = (value) => String(value || '').trim().toUpperCase();

  const writeCartPricingToForms = () => {
    document.querySelectorAll(formSelector).forEach((form) => {
      const fieldset = methodFieldset(form);
      if (!fieldset?.dataset) return;
      fieldset.dataset.cartSubtotalCents =
        cartPricing.subtotalCents === null ? '' : String(cartPricing.subtotalCents);
      fieldset.dataset.cartCurrency = cartPricing.currency;
    });
  };

  const setCartPricing = (subtotalCents, currency, { writeToForms = false } = {}) => {
    cartPricing = {
      subtotalCents: parseCents(subtotalCents),
      currency: normalizeCurrency(currency),
    };
    if (writeToForms) writeCartPricingToForms();
    syncCourierTipControls();
  };

  const readRenderedCartPricing = (forms) => {
    const rendered = forms.map((form) => {
      const dataset = methodFieldset(form)?.dataset;
      return {
        subtotalCents: parseCents(dataset?.cartSubtotalCents),
        currency: normalizeCurrency(dataset?.cartCurrency),
        minimumCents: parseCents(dataset?.localDeliveryMinimumCents),
      };
    });
    const valid = rendered.length > 0 && rendered.every(
      (entry) =>
        entry.subtotalCents !== null &&
        entry.currency &&
        entry.minimumCents === localDeliveryMinimumCents
    );
    const states = new Set(
      rendered.map((entry) => `${entry.subtotalCents}:${entry.currency}:${entry.minimumCents}`)
    );
    if (!valid || states.size !== 1) {
      setCartPricing(null, '');
      return;
    }
    setCartPricing(rendered[0].subtotalCents, rendered[0].currency);
  };

  const localDeliveryEligibility = (method = selectedMethod) => {
    if (method !== 'local_delivery') return { eligible: true, message: '' };
    if (
      cartPricing.subtotalCents === null ||
      cartPricing.currency !== localDeliveryCurrency
    ) {
      return { eligible: false, message: localDeliveryVerificationMessage };
    }
    if (cartPricing.subtotalCents < localDeliveryMinimumCents) {
      return { eligible: false, message: localDeliveryMinimumMessage };
    }
    return { eligible: true, message: '' };
  };

  const syncLocalDeliveryEligibility = () => {
    const localAvailable =
      cartPricing.currency === localDeliveryCurrency &&
      cartPricing.subtotalCents !== null &&
      cartPricing.subtotalCents >= localDeliveryMinimumCents;
    document.querySelectorAll(formSelector).forEach((form) => {
      formControls(form, methodSelector).forEach((option) => {
        if (option.value === 'local_delivery') option.disabled = !localAvailable;
      });
    });
    const eligibility = localDeliveryEligibility();
    setMethodError(eligibility.message);
    return eligibility.eligible;
  };

  const syncLegacyInputs = (form, selectedMethod) => {
    const deliveryMethod = formControl(form, '[data-legacy-delivery-method]');
    const localDelivery = formControl(form, '[data-legacy-local-delivery]');
    const shipping = formControl(form, '[data-legacy-shipping]');
    if (deliveryMethod) {
      deliveryMethod.value = selectedMethod === 'local_delivery' ? 'Local delivery' : selectedMethod === 'shipping' ? 'Shipping' : '';
    }
    if (localDelivery) localDelivery.value = selectedMethod === 'local_delivery' ? 'Yes' : 'No';
    if (shipping) shipping.value = selectedMethod === 'shipping' ? 'Yes' : 'No';
  };

  const textAttributeName = (input) => {
    if (input.matches('[name="attributes[Referral]"]')) return 'Referral';
    if (input.matches('[name="attributes[Special Delivery Instructions]"]')) return 'Special Delivery Instructions';
    return null;
  };

  const synchronizeTextAttribute = (name, value) => {
    canonicalTextAttributes.set(name, value);
    document.querySelectorAll(formSelector).forEach((form) => {
      const input = formControl(form, `[name="attributes[${name}]"]`);
      if (input && input.value !== value) input.value = value;
    });
  };

  const initializeCanonicalTextAttributes = (forms) => {
    ['Referral', 'Special Delivery Instructions'].forEach((name) => {
      if (canonicalTextAttributes.has(name)) return;
      const values = forms.map((form) => formControl(form, `[name="attributes[${name}]"]`)?.value || '');
      canonicalTextAttributes.set(name, values.find((value) => value) || '');
    });
  };

  const deliveryWindowSelectionIsValid = (
    date = requestedDeliveryDate,
    windowValue = requestedDeliveryWindow
  ) => {
    if (deliveryWindowState.status !== 'ready') return false;
    const dateOption = deliveryWindowState.dates.get(date);
    return Boolean(
      dateOption && dateOption.windows.some((entry) => entry.value === windowValue)
    );
  };

  const replaceSelectOptions = (select, options, selectedValue) => {
    if (!select) return;
    const signature = JSON.stringify(options);
    if (select.dataset.deliveryOptionsSignature !== signature) {
      const optionElements = options.map(({ value, label }) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        return option;
      });
      select.replaceChildren(...optionElements);
      select.dataset.deliveryOptionsSignature = signature;
    }
    select.value = options.some((entry) => entry.value === selectedValue) ? selectedValue : '';
  };

  const syncDeliveryWindowControls = () => {
    const localSelected = selectedMethod === 'local_delivery';
    const ready = deliveryWindowState.status === 'ready';
    if (ready && !deliveryWindowState.dates.has(requestedDeliveryDate)) {
      requestedDeliveryDate = '';
      requestedDeliveryWindow = '';
    } else if (ready && requestedDeliveryDate) {
      const windows = deliveryWindowState.dates.get(requestedDeliveryDate)?.windows || [];
      if (!windows.some((entry) => entry.value === requestedDeliveryWindow)) {
        requestedDeliveryWindow = '';
      }
    }

    const statusMessage =
      deliveryWindowState.status === 'loading'
        ? 'Loading available delivery times...'
        : deliveryWindowState.status === 'error'
          ? 'Delivery times are temporarily unavailable. Try again or choose shipping.'
          : 'Choose a delivery date and one-hour window. Times are Eastern.';
    const dateOptions = [
      { value: '', label: 'Select a date' },
      ...(ready
        ? [...deliveryWindowState.dates.values()].map(({ value, label }) => ({ value, label }))
        : []),
    ];
    const windowOptions = [
      { value: '', label: 'Select a time' },
      ...(ready && requestedDeliveryDate
        ? deliveryWindowState.dates.get(requestedDeliveryDate)?.windows || []
        : []),
    ];

    document.querySelectorAll(formSelector).forEach((form) => {
      const fieldset = deliveryWindowFieldset(form);
      if (fieldset) {
        fieldset.hidden = !localSelected;
        const status = fieldset.querySelector('[data-delivery-window-status]');
        if (status && status.textContent !== statusMessage) status.textContent = statusMessage;
      }
      const date = formControl(form, deliveryDateSelector);
      const windowControl = formControl(form, deliveryWindowSelector);
      replaceSelectOptions(date, dateOptions, requestedDeliveryDate);
      replaceSelectOptions(windowControl, windowOptions, requestedDeliveryWindow);
      [date, windowControl].forEach((control) => {
        if (!control) return;
        control.disabled = !localSelected || !ready;
        control.required = localSelected;
        control.setCustomValidity('');
        setOptionInvalid(control, false);
      });
    });
  };

  const parseCourierTipPercent = (value) => {
    const normalized = String(value ?? '').trim();
    if (!/^\d+$/.test(normalized)) return null;
    const parsed = Number(normalized);
    return courierTipPercentChoices.has(parsed) ? parsed : null;
  };

  const parseLegacyCourierTipPercent = (value) => {
    const normalized = String(value ?? '').trim();
    if (!/^\d+$/.test(normalized)) return null;
    const parsed = Number(normalized);
    return legacyCourierTipPercentChoices.has(parsed) ? parsed : null;
  };

  const migratedCourierTipPercent = (attributes, method) => {
    if (method !== 'local_delivery') return { percent: null, upgrade: false };
    const version = String(attributes['TP Courier Tip Contract Version'] || '').trim();
    if (version === String(courierTipContractVersion)) {
      return {
        percent: parseCourierTipPercent(attributes['TP Courier Tip Percent']),
        upgrade: false,
      };
    }
    if (version !== String(legacyCourierTipContractVersion)) {
      return { percent: null, upgrade: false };
    }
    const legacyPercent = parseLegacyCourierTipPercent(
      attributes['TP Courier Tip Percent']
    );
    if (legacyPercent === null) return { percent: null, upgrade: false };
    return {
      percent: courierTipPercentChoices.has(legacyPercent)
        ? legacyPercent
        : defaultCourierTipPercent,
      upgrade: true,
    };
  };

  const matchesCourierTipCapability = (tip, version, choices) =>
    typeof tip === 'object' &&
    tip !== null &&
    !Array.isArray(tip) &&
    typeof tip.enabled === 'boolean' &&
    tip.contract_version === version &&
    tip.default_percent === defaultCourierTipPercent &&
    Array.isArray(tip.percent_choices) &&
    tip.percent_choices.length === choices.size &&
    tip.percent_choices.every(
      (percent, index) => percent === [...choices][index]
    );

  const courierTipDisplayAmounts = () => {
    if (
      cartPricing.currency !== localDeliveryCurrency ||
      cartPricing.subtotalCents === null ||
      !courierTipPercentChoices.has(selectedCourierTipPercent) ||
      cartPricing.subtotalCents >
        Math.floor((Number.MAX_SAFE_INTEGER - 50) / Math.max(selectedCourierTipPercent, 1))
    ) {
      return null;
    }
    const tipCents = Math.floor(
      (cartPricing.subtotalCents * selectedCourierTipPercent + 50) / 100
    );
    return {
      tip: `$${(tipCents / 100).toFixed(2)}`,
      total: `$${((cartPricing.subtotalCents + tipCents) / 100).toFixed(2)}`,
    };
  };

  const syncCourierTipControls = () => {
    const available = selectedMethod === 'local_delivery' && courierTipCapabilityEnabled;
    if (available && !courierTipPercentChoices.has(selectedCourierTipPercent)) {
      selectedCourierTipPercent = defaultCourierTipPercent;
    }
    if (
      !available &&
      (selectedMethod !== 'local_delivery' || courierTipCapabilityReady)
    ) {
      selectedCourierTipPercent = null;
    }

    document.querySelectorAll(formSelector).forEach((form) => {
      const fieldset = courierTipFieldset(form);
      if (fieldset) fieldset.hidden = !available;
      const displayedAmounts = available ? courierTipDisplayAmounts() : null;
      const amount = fieldset?.querySelector('[data-courier-tip-amount]');
      const total = fieldset?.querySelector('[data-courier-tip-total]');
      if (amount) amount.textContent = displayedAmounts?.tip || '--';
      if (total) total.textContent = displayedAmounts?.total || '--';
      formControls(form, courierTipSelector).forEach((option) => {
        const percent = parseCourierTipPercent(option.value);
        option.checked = available && percent === selectedCourierTipPercent;
        option.disabled = !available;
        option.required = available;
        option.setCustomValidity('');
        setOptionInvalid(option, false);
      });
      const version = formControl(form, '[data-courier-tip-contract-version]');
      if (version) {
        version.value = available ? String(courierTipContractVersion) : '';
      }
    });
  };

  const parseDeliveryWindowResponse = (payload) => {
    if (
      !payload ||
      payload.contract_version !== deliveryWindowContractVersion ||
      payload.timezone !== deliveryWindowTimezone ||
      !Array.isArray(payload.dates)
    ) {
      throw new Error('TP returned an invalid delivery-window contract.');
    }

    const dates = new Map();
    payload.dates.forEach((entry) => {
      const value = String(entry?.value || '').trim();
      const label = String(entry?.label || '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !label || !Array.isArray(entry.windows)) {
        throw new Error('TP returned an invalid delivery date.');
      }
      const windows = entry.windows.map((windowEntry) => {
        const windowValue = String(windowEntry?.value || '').trim();
        const windowLabel = String(windowEntry?.label || '').trim();
        if (!/^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/.test(windowValue) || !windowLabel) {
          throw new Error('TP returned an invalid delivery window.');
        }
        return { value: windowValue, label: windowLabel };
      });
      if (!windows.length || dates.has(value)) {
        throw new Error('TP returned an invalid delivery-window schedule.');
      }
      dates.set(value, { value, label, windows });
    });
    if (!dates.size) throw new Error('TP returned no available delivery windows.');

    const tip = payload.courier_tip;
    if (tip !== null && tip !== undefined) {
      const exactCurrentContract = matchesCourierTipCapability(
        tip,
        courierTipContractVersion,
        courierTipPercentChoices
      );
      const exactLegacyContract = matchesCourierTipCapability(
        tip,
        legacyCourierTipContractVersion,
        legacyCourierTipPercentChoices
      );
      if (!exactCurrentContract && !exactLegacyContract) {
        throw new Error('TP returned an invalid courier-tip contract.');
      }
      return {
        dates,
        courierTipEnabled: exactCurrentContract && tip.enabled === true,
      };
    }
    return { dates, courierTipEnabled: false };
  };

  const loadDeliveryWindows = ({ force = false } = {}) => {
    if (!force && deliveryWindowState.status === 'ready') return Promise.resolve(true);
    if (deliveryWindowRequest) {
      if (!force) return deliveryWindowRequest;
      if (forcedDeliveryWindowRequest) return forcedDeliveryWindowRequest;
      const queuedRequest = deliveryWindowRequest
        .then(() => loadDeliveryWindows({ force: true }))
        .finally(() => {
          if (forcedDeliveryWindowRequest === queuedRequest) {
            forcedDeliveryWindowRequest = null;
          }
        });
      forcedDeliveryWindowRequest = queuedRequest;
      return queuedRequest;
    }
    deliveryWindowState = { status: 'loading', dates: new Map() };
    syncDeliveryWindowControls();
    deliveryWindowRequest = requestJson(
      '/apps/eden-tp/delivery-windows?courier_tip_contract_version=2',
      { method: 'GET', cache: 'no-store', headers: { Accept: 'application/json' } },
      'Delivery-window request'
    )
      .then((payload) => {
        const parsed = parseDeliveryWindowResponse(payload);
        deliveryWindowState = { status: 'ready', dates: parsed.dates };
        courierTipCapabilityReady = true;
        courierTipCapabilityEnabled = parsed.courierTipEnabled;
        if (
          courierTipCapabilityEnabled &&
          !courierTipPercentChoices.has(selectedCourierTipPercent)
        ) {
          selectedCourierTipPercent = defaultCourierTipPercent;
        }
        syncDeliveryWindowControls();
        syncCourierTipControls();
        return true;
      })
      .catch((error) => {
        deliveryWindowState = { status: 'error', dates: new Map() };
        courierTipCapabilityReady = true;
        courierTipCapabilityEnabled = false;
        syncDeliveryWindowControls();
        syncCourierTipControls();
        console.error('Unable to load delivery windows.', error);
        return false;
      })
      .finally(() => {
        deliveryWindowRequest = null;
      });
    return deliveryWindowRequest;
  };

  const writeTpIdToForms = (value) => {
    const nextTpId = String(value || '').trim();
    if (tpId && nextTpId !== tpId) resetDiscountVerification();
    tpId = nextTpId;
    document.querySelectorAll(formSelector).forEach((form) => {
      const input = formControl(form, '[data-tp-id]');
      if (input) input.value = tpId;
    });
  };

  const createTpId = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : generateUuid();

  const rotateTpId = ({ preserveCourierTipSelection = false } = {}) => {
    const previousTpId = tpId;
    let nextTpId = createTpId();
    if (nextTpId === previousTpId) nextTpId = createTpId();
    if (!nextTpId || nextTpId === previousTpId) {
      throw new Error('Unable to rotate the secure TP_ID for this cart.');
    }
    exhaustedUnverifiedAddressFingerprint = '';
    if (!preserveCourierTipSelection) {
      selectedCourierTipPercent = courierTipCapabilityEnabled
        ? defaultCourierTipPercent
        : null;
    }
    document.querySelectorAll(formSelector).forEach((form) => {
      const state = confirmationState(form);
      state.acceptedAddressFingerprint = '';
      state.acknowledgedUnverifiedAddressFingerprint = '';
      if (
        state.type === 'address_correction_required' ||
        state.type === 'address_confirmation_required'
      ) {
        clearConfirmationPrompt(form);
      }
    });
    writeTpIdToForms(nextTpId);
    tpIdPersistenceStarted = true;
  };

  const buildAttributes = (form) => {
    if (!ensureTpId()) {
      throw new Error('Unable to establish a secure TP_ID for this cart.');
    }
    syncLegacyInputs(form, selectedMethod);

    const includesCourierTip =
      selectedMethod === 'local_delivery' &&
      courierTipCapabilityEnabled &&
      courierTipPercentChoices.has(selectedCourierTipPercent);

    return {
      'TP Fulfillment Method': selectedMethod,
      'TP Fulfillment Contract Version': String(fulfillmentContractVersion),
      'TP Delivery Window Contract Version': selectedMethod === 'local_delivery'
        ? String(deliveryWindowContractVersion)
        : '',
      'TP Shipping Service': selectedMethod === 'shipping' ? selectedShippingService : '',
      'TP Requested Delivery Date': selectedMethod === 'local_delivery' ? requestedDeliveryDate : '',
      'TP Requested Delivery Window': selectedMethod === 'local_delivery' ? requestedDeliveryWindow : '',
      'TP Requested Delivery Timezone': selectedMethod === 'local_delivery' ? deliveryWindowTimezone : '',
      'TP Courier Tip Percent':
        includesCourierTip
          ? String(selectedCourierTipPercent)
          : '',
      'TP Courier Tip Contract Version':
        includesCourierTip
          ? String(courierTipContractVersion)
          : '',
      'Delivery method': selectedMethod === 'local_delivery' ? 'Local delivery' : selectedMethod === 'shipping' ? 'Shipping' : '',
      'NYC delivery': selectedMethod === 'local_delivery' ? 'Yes' : 'No',
      'Shipping outside NYC': selectedMethod === 'shipping' ? 'Yes' : 'No',
      Referral: canonicalTextAttributes.get('Referral') || '',
      'Special Delivery Instructions': canonicalTextAttributes.get('Special Delivery Instructions') || '',
      TP_ID: tpId,
    };
  };

  const cartRoot = () => window.Shopify?.routes?.root || '/';

  const responseDetailMessage = (detail) => {
    const messages = Array.isArray(detail)
      ? detail
          .map((entry) => (typeof entry?.msg === 'string' ? entry.msg : ''))
          .filter(Boolean)
      : [
          typeof detail === 'string'
            ? detail
            : typeof detail?.message === 'string'
              ? detail.message
              : '',
        ];
    const sanitized = messages
      .join('; ')
      .replace(/[\u0000-\u001f\u007f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return sanitized.length > 240 ? `${sanitized.slice(0, 237)}...` : sanitized;
  };

  const safeSuggestedValue = (value, { allowBlank = false, maxLength = 200 } = {}) => {
    if (allowBlank && (value === null || value === undefined || value === '')) return '';
    if (typeof value !== 'string') return null;
    const normalized = value.trim();
    if ((!normalized && !allowBlank) || normalized.length > maxLength) return null;
    return /[\u0000-\u001f\u007f]/.test(normalized) ? null : normalized;
  };

  const safeExpiry = (value) => {
    const normalized = safeSuggestedValue(value, { maxLength: 80 });
    return normalized && !Number.isNaN(Date.parse(normalized)) ? normalized : null;
  };

  const expiryHasPassed = (value) => {
    const timestamp = Date.parse(String(value || ''));
    return Number.isFinite(timestamp) && timestamp <= Date.now();
  };

  const safeEnteredAddress = (candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
    const enteredAddress = {
      address1: safeSuggestedValue(candidate.address1),
      address2: safeSuggestedValue(candidate.address2, { allowBlank: true }),
      city: safeSuggestedValue(candidate.city),
      province: safeSuggestedValue(candidate.province, { maxLength: 50 }),
      zip: safeSuggestedValue(candidate.zip, { maxLength: 20 }),
      country_code: safeSuggestedValue(candidate.country_code, { maxLength: 2 }),
    };
    if (Object.values(enteredAddress).some((value) => value === null)) return null;
    enteredAddress.country_code = enteredAddress.country_code.toUpperCase();
    return enteredAddress.country_code === 'US' ? enteredAddress : null;
  };

  const structuredValidationDetail = (detail) => {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
    const message = responseDetailMessage(detail);
    if (detail.code === 'discount_verification_required') {
      const challenge = detail.challenge;
      if (!challenge || typeof challenge !== 'object' || Array.isArray(challenge)) return null;
      const challengeId = safeSuggestedValue(challenge.id, { maxLength: 200 });
      const emailMasked = safeSuggestedValue(challenge.email_masked, { maxLength: 254 });
      const expiresAt = safeExpiry(challenge.expires_at);
      if (!challengeId || !emailMasked || !expiresAt) return null;
      return {
        code: detail.code,
        message: message || 'Verify your email before completing this discounted order.',
        challengeId,
        emailMasked,
        expiresAt,
      };
    }
    if (
      ['discount_verification_invalid', 'discount_verification_expired',
        'discount_verification_locked',
        'discount_verification_rate_limited'].includes(detail.code)
    ) {
      return {
        code: detail.code,
        message,
      };
    }
    if (detail.code === 'courier_tip_contract_recovery_required') {
      if (detail.preserve_tp_id !== true) return null;
      return {
        code: detail.code,
        message: message || 'Your delivery tip was updated. Review it and complete your order again.',
        preserveTpId: true,
      };
    }
    if (detail.code === 'email_domain_suggestion') {
      const suggestedEmail = safeSuggestedValue(detail.suggested_email, { maxLength: 254 });
      if (
        !suggestedEmail ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(suggestedEmail)
      ) {
        return null;
      }
      return {
        code: detail.code,
        message: message || 'Please confirm the suggested email address.',
        suggestedEmail,
      };
    }
    if (detail.code === 'address_invalid') {
      return {
        code: detail.code,
        message: message || 'Enter a valid delivery address.',
      };
    }
    if (detail.code === 'address_confirmation_required') {
      const enteredAddress = safeEnteredAddress(detail.entered_address);
      if (!enteredAddress) return null;
      return {
        code: detail.code,
        message: message || 'Check this delivery address or confirm it exactly as entered.',
        enteredAddress,
      };
    }
    if (detail.code !== 'address_correction_required') return null;

    const candidate = detail.suggested_address;
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
    const suggestedAddress = {
      first_name: safeSuggestedValue(candidate.first_name),
      last_name: safeSuggestedValue(candidate.last_name),
      address1: safeSuggestedValue(candidate.address1),
      address2: safeSuggestedValue(candidate.address2, { allowBlank: true }),
      city: safeSuggestedValue(candidate.city),
      province: safeSuggestedValue(candidate.province, { maxLength: 50 }),
      zip: safeSuggestedValue(candidate.zip, { maxLength: 20 }),
      country_code: safeSuggestedValue(candidate.country_code, { maxLength: 2 }),
      phone: safeSuggestedValue(candidate.phone, { maxLength: 40 }),
    };
    if (Object.values(suggestedAddress).some((value) => value === null)) return null;
    suggestedAddress.country_code = suggestedAddress.country_code.toUpperCase();
    if (suggestedAddress.country_code !== 'US') return null;
    return {
      code: detail.code,
      message: message || 'Please confirm the corrected delivery address.',
      suggestedAddress,
    };
  };

  const requestJson = async (url, options = {}, requestName = 'Request') => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeoutId;
    const request = (async () => {
      const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (!response.ok) {
        let responseBody = null;
        try {
          responseBody = await response.json();
        } catch {
          // Preserve the HTTP status when the provider does not return JSON.
        }
        const error = new Error(`${requestName} failed with status ${response.status}`);
        error.status = response.status;
        error.userMessage = responseDetailMessage(responseBody?.detail);
        error.validationDetail = structuredValidationDetail(responseBody?.detail);
        throw error;
      }
      return response.json();
    })();
    const timeout = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => {
        if (controller) controller.abort();
        reject(new Error(`${requestName} timed out.`));
      }, cartRequestTimeoutMs);
    });

    try {
      return await Promise.race([request, timeout]);
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const requestCartJson = (path, options = {}) =>
    requestJson(`${cartRoot()}${path}`, options, 'Cart request');

  const validateCartResponse = (cart) => {
    if (!cart || typeof cart.attributes !== 'object' || cart.attributes === null) {
      throw new Error('Shopify returned an invalid cart response.');
    }
    if (typeof cart.token !== 'string' || !cart.token.trim()) {
      throw new Error('Shopify did not return a cart identity.');
    }
    if (!Number.isSafeInteger(cart.item_count) || cart.item_count < 0) {
      throw new Error('Shopify did not return a valid cart item count.');
    }
    return cart;
  };

  const observeCartIdentity = (cart) => {
    const nextCartToken = cart.token.trim();
    const cartIdentityChanged = Boolean(cartToken && cartToken !== nextCartToken);
    const cartBecameEmpty = cartItemCount !== null && cartItemCount !== 0 && cart.item_count === 0;
    if (!cartToken) {
      cartToken = nextCartToken;
    }
    cartToken = nextCartToken;
    cartItemCount = cart.item_count;
    if (!cartIdentityChanged && !cartBecameEmpty) return false;

    rotateTpId();
    return true;
  };

  const fetchCart = async () =>
    validateCartResponse(
      await requestCartJson('cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
    );

  const persistAttributes = async (attributes) => {
    const cart = validateCartResponse(
      await requestCartJson('cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ attributes }),
      })
    );
    invalidateDiscountVerificationForCart(cart);

    for (const [name, expectedValue] of Object.entries(attributes)) {
      const actualValue = cart.attributes[name] ?? '';
      if (String(actualValue) !== String(expectedValue)) {
        throw new Error(`Cart attribute update was not confirmed for ${name}.`);
      }
    }
    return cart;
  };

  const finalOrderCart = async () =>
    validateCartResponse(
      await requestCartJson('cart.js?tp_order_submit=1', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
    );

  const requiredOrderValue = (form, name) => {
    const value = String(formControl(form, `[name="${name}"]`)?.value || '').trim();
    if (!value) throw new Error(`Missing required order field: ${name}`);
    return value;
  };

  const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

  const emailDomainAcknowledged = (form, email) => {
    const state = confirmationState(form);
    return state.emailDomainAcknowledged && normalizeEmail(state.acknowledgedEmail) === normalizeEmail(email);
  };

  const unverifiedAddressAcknowledged = (form, address) => {
    const state = confirmationState(form);
    return Boolean(
      state.acknowledgedUnverifiedAddressFingerprint &&
      state.acknowledgedUnverifiedAddressFingerprint === addressFingerprint(address)
    );
  };

  const discountCodeFromCart = (cart) => {
    const codes = new Set();
    (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).forEach((entry) => {
      const code = String(entry?.code || '').trim();
      if (code && entry?.applicable !== false) codes.add(code);
    });
    (Array.isArray(cart.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : []
    ).forEach((entry) => {
      const code = entry?.type === 'discount_code' ? String(entry.title || '').trim() : '';
      if (code) codes.add(code);
    });
    return codes.size === 1 ? codes.values().next().value : null;
  };

  const cartHasDiscount = (cart) => {
    if ((parseCents(cart.total_discount) || 0) > 0) return true;
    if (
      Array.isArray(cart.cart_level_discount_applications) &&
      cart.cart_level_discount_applications.length > 0
    ) {
      return true;
    }
    return (Array.isArray(cart.items) ? cart.items : []).some(
      (item) =>
        (parseCents(item?.total_discount) || 0) > 0 ||
        (Array.isArray(item?.line_level_discount_allocations) &&
          item.line_level_discount_allocations.length > 0)
    );
  };

  const cartVerificationFingerprint = (cart) => {
    const token = String(cart?.token || '').trim();
    const currency = normalizeCurrency(cart?.currency);
    const totalPrice = parseCents(cart?.total_price);
    const totalDiscount = parseCents(cart?.total_discount) || 0;
    if (!token || !currency || totalPrice === null || !Array.isArray(cart?.items)) return '';
    const items = cart.items.map((item) => [
      String(item?.key || '').trim(),
      Number(item?.variant_id),
      Number(item?.quantity),
    ]);
    const discountCodes = [
      ...(Array.isArray(cart.discount_codes) ? cart.discount_codes : [])
        .filter((entry) => entry?.applicable !== false)
        .map((entry) => String(entry?.code || '').trim()),
      ...(Array.isArray(cart.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [])
        .filter((entry) => entry?.type === 'discount_code')
        .map((entry) => String(entry?.title || '').trim()),
    ].filter(Boolean).sort();
    return JSON.stringify([token, currency, totalPrice, totalDiscount, items, discountCodes]);
  };

  const discountVerificationBinding = (form, cart) => {
    const email = normalizeEmail(formControl(form, emailSelector)?.value);
    const cartId = String(cart?.token || '').trim();
    const finalTpId = String(cart?.attributes?.TP_ID || '').trim();
    const cartFingerprint = cartVerificationFingerprint(cart);
    if (!email || !cartId || !finalTpId || !cartFingerprint) return null;
    return { email, cartId, tpId: finalTpId, cartFingerprint };
  };

  const discountVerificationBindingMatches = (form, cart = null) => {
    const binding = discountVerification.binding;
    if (!binding) return false;
    if (
      binding.email !== normalizeEmail(formControl(form, emailSelector)?.value) ||
      binding.cartId !== cartToken ||
      binding.tpId !== tpId
    ) {
      return false;
    }
    return !cart || binding.cartFingerprint === cartVerificationFingerprint(cart);
  };

  const discountVerificationToken = (form, cart) => {
    if (discountVerification.status !== 'verified' || !discountVerification.proofToken) return '';
    if (expiryHasPassed(discountVerification.proofExpiresAt)) {
      resetDiscountVerification();
      return '';
    }
    if (!discountVerificationBindingMatches(form, cart)) {
      resetDiscountVerification();
      return '';
    }
    return discountVerification.proofToken;
  };

  const invalidateDiscountVerificationForCart = (cart) => {
    if (discountVerification.status === 'idle' || !discountVerification.binding) return;
    const cartFingerprint = cartVerificationFingerprint(cart);
    if (
      String(cart?.token || '').trim() !== discountVerification.binding.cartId ||
      (cartFingerprint && cartFingerprint !== discountVerification.binding.cartFingerprint)
    ) {
      resetDiscountVerification();
    }
  };

  const showDiscountVerificationChallenge = (form, detail, cart) => {
    const prompt = discountVerificationPrompt(form);
    const binding = discountVerificationBinding(form, cart);
    if (!prompt || !binding) return false;
    discountVerification.status = 'challenge';
    discountVerification.revision += 1;
    discountVerification.challengeId = detail.challengeId;
    discountVerification.emailMasked = detail.emailMasked;
    discountVerification.expiresAt = detail.expiresAt;
    discountVerification.message = detail.message;
    discountVerification.errorMessage = '';
    discountVerification.attempts = 0;
    discountVerification.proofToken = '';
    discountVerification.proofExpiresAt = '';
    discountVerification.binding = binding;
    syncDiscountVerificationPrompt(form);
    clearPersistenceError(form);
    const code = prompt.querySelector(discountVerificationCodeSelector);
    if (typeof code?.focus === 'function') code.focus();
    else if (typeof prompt.focus === 'function') prompt.focus();
    return true;
  };

  const orderPayload = (form, cart) => {
    const currency = normalizeCurrency(cart.currency);
    const finalTpId = String(cart.attributes.TP_ID || '').trim();
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        finalTpId
      ) ||
      finalTpId !== tpId
    ) {
      throw new Error('The final cart response did not confirm the current TP_ID.');
    }
    if (currency !== 'USD') throw new Error('Only USD carts can be submitted.');
    if (!Array.isArray(cart.items) || cart.items.length === 0 || cart.item_count < 1) {
      throw new Error('The final cart response did not include order items.');
    }

    const lineItems = cart.items.map((item) => {
      const variantId = Number(item?.variant_id);
      const quantity = Number(item?.quantity);
      if (!Number.isSafeInteger(variantId) || variantId < 1) {
        throw new Error('The final cart response included an invalid variant.');
      }
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        throw new Error('The final cart response included an invalid quantity.');
      }
      return { variant_id: variantId, quantity };
    });
    if (lineItems.reduce((total, item) => total + item.quantity, 0) !== cart.item_count) {
      throw new Error('The final cart item count did not match its line items.');
    }

    const email = requiredOrderValue(form, 'contact[email]');
    const emailConfirmation = requiredOrderValue(form, 'contact[email_confirmation]');
    if (normalizeEmail(email) !== normalizeEmail(emailConfirmation)) {
      throw new Error('The email confirmation does not match.');
    }
    const firstName = requiredOrderValue(form, 'shipping_address[first_name]');
    const lastName = requiredOrderValue(form, 'shipping_address[last_name]');
    const phone = requiredOrderValue(form, 'contact[phone]');
    const countryCode = requiredOrderValue(form, 'shipping_address[country_code]').toUpperCase();
    if (countryCode !== 'US') throw new Error('Only US delivery addresses are supported.');
    const fulfillmentMethod = String(cart.attributes['TP Fulfillment Method'] || '').trim();
    const shippingService = String(cart.attributes['TP Shipping Service'] || '').trim();
    const finalDeliveryDate = String(
      cart.attributes['TP Requested Delivery Date'] || ''
    ).trim();
    const finalDeliveryWindow = String(
      cart.attributes['TP Requested Delivery Window'] || ''
    ).trim();
    const finalDeliveryTimezone = String(
      cart.attributes['TP Requested Delivery Timezone'] || ''
    ).trim();
    const finalDeliveryWindowVersion = String(
      cart.attributes['TP Delivery Window Contract Version'] || ''
    ).trim();
    const finalCourierTipPercent = String(
      cart.attributes['TP Courier Tip Percent'] || ''
    ).trim();
    const finalCourierTipVersion = String(
      cart.attributes['TP Courier Tip Contract Version'] || ''
    ).trim();
    if (!['local_delivery', 'shipping'].includes(fulfillmentMethod)) {
      throw new Error('The final cart response did not confirm the fulfillment method.');
    }
    if (
      (fulfillmentMethod === 'local_delivery' && shippingService) ||
      (fulfillmentMethod === 'shipping' && !shippingServices.has(shippingService))
    ) {
      throw new Error('The final cart response did not confirm the shipping service.');
    }
    if (fulfillmentMethod === 'local_delivery') {
      if (
        finalDeliveryTimezone !== deliveryWindowTimezone ||
        finalDeliveryWindowVersion !== String(deliveryWindowContractVersion) ||
        !deliveryWindowSelectionIsValid(finalDeliveryDate, finalDeliveryWindow)
      ) {
        throw new Error('The final cart response did not confirm the requested delivery time.');
      }
    } else if (
      finalDeliveryDate ||
      finalDeliveryWindow ||
      finalDeliveryTimezone ||
      finalDeliveryWindowVersion
    ) {
      throw new Error('Shipping cannot include a local delivery time.');
    }
    const parsedCourierTipPercent = parseCourierTipPercent(finalCourierTipPercent);
    const hasCourierTipContract =
      parsedCourierTipPercent !== null &&
      finalCourierTipVersion === String(courierTipContractVersion);
    if (fulfillmentMethod === 'local_delivery' && courierTipCapabilityEnabled) {
      if (!hasCourierTipContract) {
        throw new Error('The final cart response did not confirm the delivery tip.');
      }
    } else if (
      fulfillmentMethod === 'local_delivery' &&
      (finalCourierTipPercent || finalCourierTipVersion) &&
      !hasCourierTipContract
    ) {
      throw new Error('The final cart response included an invalid delivery tip.');
    } else if (fulfillmentMethod === 'shipping' && (finalCourierTipPercent || finalCourierTipVersion)) {
      throw new Error('This order cannot include a delivery tip.');
    }
    const referral = String(cart.attributes.Referral || '');
    const specialInstructions = String(
      cart.attributes['Special Delivery Instructions'] || ''
    );
    if (referral.length > 500 || specialInstructions.length > 500) {
      throw new Error('The final cart response included oversized order instructions.');
    }

    const shippingAddress = {
      first_name: firstName,
      last_name: lastName,
      address1: requiredOrderValue(form, 'shipping_address[address1]'),
      address2: String(
        formControl(form, '[name="shipping_address[address2]"]')?.value || ''
      ).trim(),
      city: requiredOrderValue(form, 'shipping_address[city]'),
      province: requiredOrderValue(form, 'shipping_address[province]'),
      zip: requiredOrderValue(form, 'shipping_address[zip]'),
      country_code: countryCode,
      phone,
    };

    const payload = {
      tp_id: finalTpId,
      cart_id: cart.token.trim(),
      contact: {
        email,
        email_confirmation: emailConfirmation,
        phone,
        first_name: firstName,
        last_name: lastName,
      },
      email_domain_acknowledged: emailDomainAcknowledged(form, email),
      unverified_address_acknowledged: unverifiedAddressAcknowledged(form, shippingAddress),
      shipping_address: shippingAddress,
      line_items: lineItems,
      currency,
      discount_code: discountCodeFromCart(cart),
      has_discount: cartHasDiscount(cart),
      fulfillment_method: fulfillmentMethod,
      shipping_service: fulfillmentMethod === 'shipping' ? shippingService : null,
      requested_delivery_date:
        fulfillmentMethod === 'local_delivery' ? finalDeliveryDate : null,
      requested_delivery_window:
        fulfillmentMethod === 'local_delivery' ? finalDeliveryWindow : null,
      requested_delivery_timezone:
        fulfillmentMethod === 'local_delivery' ? finalDeliveryTimezone : null,
      delivery_window_contract_version:
        fulfillmentMethod === 'local_delivery' ? deliveryWindowContractVersion : null,
      referral: referral || null,
      special_instructions: specialInstructions || null,
    };
    if (
      fulfillmentMethod === 'local_delivery' &&
      courierTipCapabilityEnabled &&
      hasCourierTipContract
    ) {
      payload.courier_tip_percent = parsedCourierTipPercent;
      payload.courier_tip_contract_version = courierTipContractVersion;
    }
    const verificationToken = discountVerificationToken(form, cart);
    if (verificationToken) payload.discount_verification_token = verificationToken;
    return payload;
  };

  const submitOrderToTp = async (form, cart) => {
    const postOrder = (submitCart) =>
      requestJson(
        '/apps/eden-tp/order',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(orderPayload(form, submitCart)),
        },
        'Order request'
      );

    let submitCart = cart;
    let response;
    try {
      response = await postOrder(submitCart);
    } catch (error) {
      if (error?.validationDetail?.code === 'discount_verification_required') {
        error.discountVerificationCart = submitCart;
        throw error;
      }
      const staleTpId =
        error?.status === 409 &&
        error?.validationDetail?.code !== 'courier_tip_contract_recovery_required' &&
        typeof error.userMessage === 'string' &&
        error.userMessage.startsWith('This TP_ID is already bound');
      if (!staleTpId) throw error;

      rotateTpId({ preserveCourierTipSelection: true });
      await persistAttributes(buildAttributes(form));
      submitCart = await finalOrderCart();
      try {
        response = await postOrder(submitCart);
      } catch (error) {
        if (error?.validationDetail?.code === 'discount_verification_required') {
          error.discountVerificationCart = submitCart;
        }
        throw error;
      }
    }

    if (
      !response ||
      !['created', 'already_created'].includes(response.status) ||
      String(response.tp_id || '').trim() !==
        String(submitCart.attributes.TP_ID || '').trim() ||
      !String(response.order_name || '').trim()
    ) {
      throw new Error('TP returned an invalid order confirmation.');
    }

    const redirectPath =
      `/apps/eden-tp/confirmation?tp_id=${encodeURIComponent(response.tp_id.trim())}`;
    try {
      const clearLineUpdates = Object.fromEntries(
        submitCart.items.map((item) => {
          const lineKey = String(item?.key || '').trim();
          if (!lineKey) throw new Error('The final cart response included an invalid line key.');
          return [lineKey, 0];
        })
      );
      if (Object.keys(clearLineUpdates).length !== submitCart.items.length) {
        throw new Error('The final cart response included duplicate line keys.');
      }
      const resetAttributes = Object.fromEntries(
        Object.keys(buildAttributes(form)).map((name) => [name, ''])
      );
      const resetCart = validateCartResponse(
        await requestCartJson('cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            updates: clearLineUpdates,
            attributes: resetAttributes,
          }),
        })
      );
      for (const name of Object.keys(resetAttributes)) {
        if (String(resetCart.attributes[name] ?? '') !== '') {
          throw new Error(`Shopify did not clear the submitted cart attribute ${name}.`);
        }
      }
      if (resetCart.item_count !== 0) throw new Error('Shopify did not confirm an empty cart.');
    } catch (cleanupError) {
      console.error('Unable to clear submitted cart after TP confirmation.', cleanupError);
    }
    window.location.assign(redirectPath);
  };

  // Serialize updates so a delayed text-field save cannot overwrite the final order save.
  const queuePersistence = (form, attributesFactory) => {
    const formId = form.id;
    const operation = persistenceQueue.then(async () => {
      try {
        const activeForm = currentForm(formId) || form;
        const attributes =
          typeof attributesFactory === 'function'
            ? attributesFactory(activeForm)
            : attributesFactory;
        let cart = await persistAttributes(attributes);
        if (observeCartIdentity(cart)) {
          const updatedForm = currentForm(formId);
          if (!updatedForm) throw new Error('The checkout form is no longer available.');
          cart = await persistAttributes(buildAttributes(updatedForm));
          if (observeCartIdentity(cart)) {
            throw new Error('The cart identity changed repeatedly while saving.');
          }
        }
        setCartPricing(cart.total_price, cart.currency, { writeToForms: true });
        syncLocalDeliveryEligibility();
        clearPersistenceError(currentForm(formId));
        return { ok: true, cart };
      } catch (error) {
        showPersistenceError(currentForm(formId));
        console.error('Unable to persist checkout attributes.', error);
        return { ok: false };
      }
    });

    persistenceQueue = operation;
    return operation;
  };

  const validateDeliveryMethod = (form) => {
    const options = formControls(form, methodSelector);
    options.forEach((option) => {
      option.setCustomValidity('');
      setOptionInvalid(option, false);
    });
    const selected = options.find((option) => option.checked) || null;
    if (selected) {
      const eligibility = localDeliveryEligibility(selected.value);
      if (eligibility.eligible && selected.value === 'local_delivery') {
        const date = formControl(form, deliveryDateSelector);
        const windowControl = formControl(form, deliveryWindowSelector);
        if (!deliveryWindowSelectionIsValid()) {
          const message = deliveryWindowState.status === 'error'
            ? 'Delivery times are temporarily unavailable. Try again or choose shipping.'
            : deliveryWindowState.status !== 'ready'
              ? 'Wait for the available delivery times to load.'
              : !requestedDeliveryDate
                ? 'Choose a delivery date.'
                : 'Choose a delivery time.';
          const target = !requestedDeliveryDate ? date : windowControl;
          setMethodError(message);
          if (target) {
            target.setCustomValidity(message);
            setOptionInvalid(target, true);
            target.reportValidity();
          }
          return false;
        }
      }
      if (eligibility.eligible && selected.value === 'shipping') {
        const serviceOptions = formControls(form, shippingServiceSelector);
        const selectedService = serviceOptions.find(
          (option) => option.checked && !option.disabled && shippingServices.has(option.value)
        );
        if (!selectedService) {
          const firstService = serviceOptions.find((option) => !option.disabled) || serviceOptions[0];
          const message = 'Choose Priority or Priority Express.';
          setMethodError(message);
          if (firstService) {
            firstService.setCustomValidity(message);
            setOptionInvalid(firstService, true);
            firstService.reportValidity();
          }
          return false;
        }
      }
      setMethodError(eligibility.message);
      setOptionInvalid(selected, !eligibility.eligible);
      return eligibility.eligible;
    }

    const firstOption = options.find((option) => !option.disabled) || options[0];
    if (firstOption) {
      const message = 'Choose local delivery or shipping.';
      setMethodError(message);
      firstOption.setCustomValidity(message);
      setOptionInvalid(firstOption, true);
      firstOption.reportValidity();
    }
    return false;
  };

  const validateEmailConfirmation = (form) => {
    const email = formControl(form, emailSelector);
    const emailConfirmation = formControl(form, emailConfirmationSelector);
    if (!email && !emailConfirmation) return true;
    if (!email || !emailConfirmation) {
      showPersistenceError(form, 'Enter and confirm your email address.');
      return false;
    }

    emailConfirmation.setCustomValidity('');
    setOptionInvalid(emailConfirmation, false);
    if (normalizeEmail(email.value) === normalizeEmail(emailConfirmation.value)) {
      clearPersistenceError(form);
      return true;
    }

    const message = 'Email addresses do not match. Re-enter your confirmation email.';
    emailConfirmation.setCustomValidity(message);
    setOptionInvalid(emailConfirmation, true);
    showPersistenceError(form, message);
    emailConfirmation.reportValidity();
    return false;
  };

  const addressFingerprint = (address) => JSON.stringify(
    [
      address.first_name,
      address.last_name,
      address.address1,
      address.address2,
      address.city,
      address.province,
      address.zip,
      address.country_code,
      address.phone,
    ].map((value) => String(value || '').trim())
  );

  const postalAddressFingerprint = (address) => JSON.stringify(
    [
      address.address1,
      address.address2,
      address.city,
      address.province,
      address.zip,
      String(address.country_code || '').toUpperCase(),
    ].map((value) => String(value || '').trim())
  );

  const currentAddress = (form) => ({
    first_name: formControl(form, '[name="shipping_address[first_name]"]')?.value,
    last_name: formControl(form, '[name="shipping_address[last_name]"]')?.value,
    address1: formControl(form, '[name="shipping_address[address1]"]')?.value,
    address2: formControl(form, '[name="shipping_address[address2]"]')?.value,
    city: formControl(form, '[name="shipping_address[city]"]')?.value,
    province: formControl(form, '[name="shipping_address[province]"]')?.value,
    zip: formControl(form, '[name="shipping_address[zip]"]')?.value,
    country_code: formControl(form, '[name="shipping_address[country_code]"]')?.value,
    phone: formControl(form, '[name="contact[phone]"]')?.value,
  });

  const writeSuggestedAddress = (form, address) => {
    const fieldNames = {
      first_name: 'shipping_address[first_name]',
      last_name: 'shipping_address[last_name]',
      address1: 'shipping_address[address1]',
      address2: 'shipping_address[address2]',
      city: 'shipping_address[city]',
      province: 'shipping_address[province]',
      zip: 'shipping_address[zip]',
      country_code: 'shipping_address[country_code]',
      phone: 'contact[phone]',
    };
    for (const [key, name] of Object.entries(fieldNames)) {
      const input = formControl(form, `[name="${name}"]`);
      if (!input) throw new Error(`The corrected address field is unavailable: ${name}`);
      input.value = address[key];
    }
  };

  const handleStructuredValidationError = (form, error) => {
    if (!error?.validationDetail) return false;
    const detail = error.validationDetail;
    if (
      error.status === 409 &&
      detail.code === 'courier_tip_contract_recovery_required' &&
      detail.preserveTpId === true
    ) {
      const errorTarget = showPersistenceError(form, detail.message);
      if (typeof errorTarget?.focus === 'function') errorTarget.focus();
      return true;
    }
    if (detail.code === 'discount_verification_rate_limited') {
      resetDiscountVerification();
      showPersistenceError(
        form,
        'Too many verification requests. Wait before trying again.'
      );
      return true;
    }
    if (
      ['discount_verification_invalid', 'discount_verification_expired',
        'discount_verification_locked'].includes(detail.code)
    ) {
      const terminalStatus = detail.code.replace('discount_verification_', '');
      const message = terminalStatus === 'expired'
        ? 'Email verification expired. Select Complete order to request a new code.'
        : terminalStatus === 'locked'
          ? 'Email verification is locked. Select Complete order to request a new code.'
          : 'Email verification is no longer valid. Select Complete order to request a new code.';
      setDiscountVerificationTerminalState(form, terminalStatus, message);
      return true;
    }
    if (error.status !== 422) return false;
    const state = confirmationState(form);
    if (detail.code === 'discount_verification_required') {
      if (!showDiscountVerificationChallenge(form, detail, error.discountVerificationCart)) {
        showPersistenceError(form, orderSubmissionErrorMessage);
      }
      return true;
    }
    if (detail.code === 'address_invalid') {
      clearConfirmationPrompt(form);
      showPersistenceError(form, detail.message);
      const address1 = formControl(form, '[name="shipping_address[address1]"]');
      if (typeof address1?.focus === 'function') address1.focus();
      return true;
    }
    if (detail.code === 'email_domain_suggestion') {
      const email = String(formControl(form, emailSelector)?.value || '').trim();
      if (emailDomainAcknowledged(form, email)) {
        clearConfirmationPrompt(form);
        showPersistenceError(form, detail.message);
        const emailInput = formControl(form, emailSelector);
        if (typeof emailInput?.focus === 'function') emailInput.focus();
        return true;
      }
      if (!showConfirmationPrompt(form, detail)) showPersistenceError(form, detail.message);
      return true;
    }

    if (detail.code === 'address_confirmation_required') {
      if (
        postalAddressFingerprint(detail.enteredAddress) !==
        postalAddressFingerprint(currentAddress(form))
      ) {
        clearConfirmationPrompt(form);
        showPersistenceError(
          form,
          'The delivery address changed. Review it and complete your order again.'
        );
        const address1 = formControl(form, '[name="shipping_address[address1]"]');
        if (typeof address1?.focus === 'function') address1.focus();
        return true;
      }
      const fingerprint = addressFingerprint(currentAddress(form));
      if (state.acknowledgedUnverifiedAddressFingerprint === fingerprint) {
        state.acknowledgedUnverifiedAddressFingerprint = '';
        exhaustedUnverifiedAddressFingerprint = fingerprint;
        clearConfirmationPrompt(form);
        showPersistenceError(form, detail.message);
        const address1 = formControl(form, '[name="shipping_address[address1]"]');
        if (typeof address1?.focus === 'function') address1.focus();
        return true;
      }
      if (!showConfirmationPrompt(form, { ...detail, addressFingerprint: fingerprint })) {
        showPersistenceError(form, detail.message);
      }
      return true;
    }

    const suggestedFingerprint = addressFingerprint(detail.suggestedAddress);
    if (
      state.acceptedAddressFingerprint === suggestedFingerprint &&
      addressFingerprint(currentAddress(form)) === suggestedFingerprint
    ) {
      clearConfirmationPrompt(form);
      showPersistenceError(form, detail.message);
      const address1 = formControl(form, '[name="shipping_address[address1]"]');
      if (typeof address1?.focus === 'function') address1.focus();
      return true;
    }
    if (!showConfirmationPrompt(form, detail)) showPersistenceError(form, detail.message);
    return true;
  };

  const handleCheckoutFieldEdit = (form, input) => {
    const state = confirmationState(form);
    if (input.matches(emailSelector) || input.matches(emailConfirmationSelector)) {
      const confirmation = formControl(form, emailConfirmationSelector);
      if (confirmation) {
        confirmation.setCustomValidity('');
        setOptionInvalid(confirmation, false);
      }
      if (input.matches(emailSelector)) {
        state.emailDomainAcknowledged = false;
        state.acknowledgedEmail = '';
      }
      resetDiscountVerification();
      if (state.type === 'email_domain_suggestion') clearConfirmationPrompt(form);
    } else {
      state.acceptedAddressFingerprint = '';
      state.acknowledgedUnverifiedAddressFingerprint = '';
      exhaustedUnverifiedAddressFingerprint = '';
      if (
        state.type === 'address_correction_required' ||
        state.type === 'address_confirmation_required'
      ) {
        clearConfirmationPrompt(form);
      }
    }
    clearPersistenceError(form);
  };

  const selectedFulfillmentCharge = () => {
    if (selectedMethod === 'local_delivery') {
      return { label: 'Local delivery', value: 'Free' };
    }
    if (selectedMethod === 'shipping' && selectedShippingService === 'priority_express') {
      return { label: 'Priority Express shipping', value: '$40.00' };
    }
    if (selectedMethod === 'shipping') {
      return { label: 'Priority shipping', value: '$15.00' };
    }
    return null;
  };

  const syncFulfillmentChargeSummary = () => {
    const charge = selectedFulfillmentCharge();
    document.querySelectorAll('[data-tp-fulfillment-charge]').forEach((summary) => {
      const label = summary.querySelector('[data-tp-fulfillment-charge-label]');
      const value = summary.querySelector('[data-tp-fulfillment-charge-value]');
      if (label) label.textContent = charge?.label || '';
      if (value) value.textContent = charge?.value || '';
      summary.hidden = !charge;
    });
  };

  const synchronizeMethodSelection = (method) => {
    selectedMethod = method;
    if (selectedMethod === 'shipping' && !shippingServices.has(selectedShippingService)) {
      selectedShippingService = 'priority';
    } else if (selectedMethod !== 'shipping') {
      selectedShippingService = '';
    }
    if (selectedMethod !== 'local_delivery') {
      requestedDeliveryDate = '';
      requestedDeliveryWindow = '';
    }
    document.querySelectorAll(formSelector).forEach((form) => {
      if (selectedMethod !== 'shipping') {
        const state = confirmationState(form);
        state.acknowledgedUnverifiedAddressFingerprint = '';
        if (state.type === 'address_confirmation_required') {
          clearConfirmationPrompt(form);
        }
      }
      formControls(form, methodSelector).forEach((option) => {
        option.checked = option.value === selectedMethod;
        option.setCustomValidity('');
        setOptionInvalid(option, false);
      });
      const shippingSelected = selectedMethod === 'shipping';
      formControls(form, shippingServiceSelector).forEach((option) => {
        option.checked = shippingSelected && option.value === selectedShippingService;
        option.disabled = !shippingSelected;
        option.required = shippingSelected;
        option.setCustomValidity('');
        setOptionInvalid(option, false);
      });
      const shippingOptions = formControl(form, '[data-shipping-service-options]');
      if (shippingOptions) shippingOptions.hidden = !shippingSelected;
      syncLegacyInputs(form, selectedMethod);
    });
    syncDeliveryWindowControls();
    syncCourierTipControls();
    if (selectedMethod === 'local_delivery') loadDeliveryWindows();
    syncFulfillmentChargeSummary();
    syncLocalDeliveryEligibility();
  };

  const ensureTpId = () => {
    const forms = [...document.querySelectorAll(formSelector)];
    if (!forms.length) return false;

    if (!tpId) {
      writeTpIdToForms(
        forms
          .map((form) => formControl(form, '[data-tp-id]')?.value || '')
          .find((value) => value) || ''
      );
    }

    if (!tpId) {
      try {
        writeTpIdToForms(createTpId());
      } catch (error) {
        forms.forEach(showPersistenceError);
        console.error('Unable to establish a secure TP_ID.', error);
        return false;
      }
    }

    writeTpIdToForms(tpId);

    if (!tpIdPersistenceStarted && !forms.some((form) => formControl(form, '[data-tp-id]')?.defaultValue)) {
      tpIdPersistenceStarted = true;
      queuePersistence(forms[0], () => ({ TP_ID: tpId }));
    }
    return true;
  };

  const deliveryMethodFromAttributes = (attributes) => {
    const canonical = String(attributes['TP Fulfillment Method'] || '').trim();
    if (canonical === 'local_delivery' || canonical === 'shipping') return canonical;

    const legacyMethod = String(attributes['Delivery method'] || '').trim().toLowerCase();
    if (legacyMethod === 'local_delivery' || legacyMethod === 'local delivery') {
      return 'local_delivery';
    }
    if (legacyMethod === 'shipping') return 'shipping';

    const localDelivery = String(attributes['NYC delivery'] || '').trim().toLowerCase();
    const shipping = String(attributes['Shipping outside NYC'] || '').trim().toLowerCase();
    if (localDelivery === 'yes' && shipping !== 'yes') return 'local_delivery';
    if (shipping === 'yes' && localDelivery !== 'yes') return 'shipping';
    return '';
  };

  const shippingServiceFromAttributes = (attributes, method) => {
    if (method !== 'shipping') return '';
    const service = String(attributes['TP Shipping Service'] || '').trim();
    return shippingServices.has(service) ? service : 'priority';
  };

  const applyCartSnapshot = (cart, { preserveFulfillmentIntent = false } = {}) => {
    invalidateDiscountVerificationForCart(cart);
    const nextCartToken = cart.token.trim();
    const previousTpId = tpId;
    const identityChanged = Boolean(cartToken && cartToken !== nextCartToken);
    const cartBecameEmpty = cartItemCount !== null && cartItemCount !== 0 && cart.item_count === 0;
    const serverTpId = String(cart.attributes.TP_ID || '').trim();
    cartToken = nextCartToken;
    cartItemCount = cart.item_count;

    const needsFreshTpId =
      !serverTpId || ((identityChanged || cartBecameEmpty) && serverTpId === previousTpId);
    if (needsFreshTpId) rotateTpId();
    else {
      writeTpIdToForms(serverTpId);
      tpIdPersistenceStarted = true;
    }

    const preserveCurrentFulfillment =
      preserveFulfillmentIntent && !identityChanged && !cartBecameEmpty;
    let upgradeCourierTipContract = false;
    if (!preserveCurrentFulfillment) {
      selectedMethod = cartBecameEmpty ? '' : deliveryMethodFromAttributes(cart.attributes);
      selectedShippingService = cartBecameEmpty
        ? ''
        : shippingServiceFromAttributes(cart.attributes, selectedMethod);
      requestedDeliveryDate = selectedMethod === 'local_delivery'
        ? String(cart.attributes['TP Requested Delivery Date'] || '').trim()
        : '';
      requestedDeliveryWindow = selectedMethod === 'local_delivery'
        ? String(cart.attributes['TP Requested Delivery Window'] || '').trim()
        : '';
      const migratedTip = migratedCourierTipPercent(
        cart.attributes,
        selectedMethod
      );
      selectedCourierTipPercent = migratedTip.percent;
      upgradeCourierTipContract = migratedTip.upgrade;
    }
    canonicalTextAttributes.set(
      'Referral',
      cartBecameEmpty ? '' : String(cart.attributes.Referral || '')
    );
    canonicalTextAttributes.set(
      'Special Delivery Instructions',
      cartBecameEmpty ? '' : String(cart.attributes['Special Delivery Instructions'] || '')
    );
    setCartPricing(cart.total_price, cart.currency, { writeToForms: true });
    synchronizeMethodSelection(selectedMethod);
    canonicalTextAttributes.forEach((value, name) => synchronizeTextAttribute(name, value));
    syncLocalDeliveryEligibility();
    return {
      needsFreshTpId,
      resetCartAttributes: cartBecameEmpty,
      upgradeCourierTipContract,
    };
  };

  const refreshCartFromServer = ({ refreshCourierTipCapability = false } = {}) => {
    if (cartRefreshInFlight) {
      if (
        !refreshCourierTipCapability ||
        cartRefreshIncludesCourierTipCapability
      ) {
        return cartRefreshInFlight;
      }
      if (queuedCourierTipCartRefresh) return queuedCourierTipCartRefresh;
      const queuedRefresh = cartRefreshInFlight
        .then(() =>
          refreshCartFromServer({ refreshCourierTipCapability: true })
        )
        .finally(() => {
          if (queuedCourierTipCartRefresh === queuedRefresh) {
            queuedCourierTipCartRefresh = null;
          }
        });
      queuedCourierTipCartRefresh = queuedRefresh;
      return queuedRefresh;
    }
    if (!document.querySelectorAll(formSelector).length) {
      return Promise.resolve({ ok: false, skipped: true });
    }

    const requestedFulfillmentRevision = fulfillmentIntentRevision;
    const operation = persistenceQueue.then(async () => {
      try {
        if (refreshCourierTipCapability) {
          await loadDeliveryWindows({ force: true });
        }
        const cart = await fetchCart();
        const {
          needsFreshTpId,
          resetCartAttributes,
          upgradeCourierTipContract,
        } = applyCartSnapshot(cart, {
          preserveFulfillmentIntent:
            fulfillmentIntentRevision !== requestedFulfillmentRevision,
        });
        if (upgradeCourierTipContract && !courierTipCapabilityReady) {
          await loadDeliveryWindows();
        }
        if (needsFreshTpId || resetCartAttributes || upgradeCourierTipContract) {
          const activeForm = document.querySelectorAll(formSelector)[0];
          if (!activeForm) throw new Error('The checkout form is no longer available.');
          const attributes = resetCartAttributes || upgradeCourierTipContract
            ? buildAttributes(activeForm)
            : { TP_ID: tpId };
          const persistedCart = await persistAttributes(attributes);
          if (persistedCart.token.trim() !== cartToken) {
            cartToken = persistedCart.token.trim();
            rotateTpId();
            throw new Error('The cart identity changed while restoring checkout state.');
          }
          setCartPricing(persistedCart.total_price, persistedCart.currency, { writeToForms: true });
          syncLocalDeliveryEligibility();
        }
        document.querySelectorAll(formSelector).forEach(clearPersistenceError);
        return { ok: true, cart };
      } catch (error) {
        document.querySelectorAll(formSelector).forEach(showPersistenceError);
        console.error('Unable to refresh checkout state.', error);
        return { ok: false };
      }
    });
    const settled = operation.finally(() => {
      if (cartRefreshInFlight === settled) {
        cartRefreshInFlight = null;
        cartRefreshIncludesCourierTipCapability = false;
      }
    });
    cartRefreshInFlight = settled;
    cartRefreshIncludesCourierTipCapability = refreshCourierTipCapability;
    persistenceQueue = settled;
    return settled;
  };

  const refresh = () => {
    ensureTpId();
    const forms = [...document.querySelectorAll(formSelector)];
    readRenderedCartPricing(forms);
    initializeCanonicalTextAttributes(forms);
    if (!selectedMethod) {
      selectedMethod = forms
        .map(
          (form) =>
            formControls(form, methodSelector).find((option) => option.checked)?.value || ''
        )
        .find((value) => value) || '';
    }
    if (selectedMethod === 'shipping' && !shippingServices.has(selectedShippingService)) {
      selectedShippingService = forms
        .map(
          (form) =>
            formControls(form, shippingServiceSelector).find((option) => option.checked)?.value || ''
        )
        .find((value) => shippingServices.has(value)) || 'priority';
    }
    if (selectedMethod === 'local_delivery' && !requestedDeliveryDate) {
      requestedDeliveryDate = forms
        .map((form) => String(formControl(form, deliveryDateSelector)?.value || '').trim())
        .find((value) => value) || '';
      requestedDeliveryWindow = forms
        .map((form) => String(formControl(form, deliveryWindowSelector)?.value || '').trim())
        .find((value) => value) || '';
    }
    if (selectedMethod === 'local_delivery' && selectedCourierTipPercent === null) {
      const renderedCourierTipPercent = forms
        .flatMap((form) => formControls(form, courierTipSelector))
        .filter((option) => option.checked)
        .map((option) => parseCourierTipPercent(option.value))
        .find((value) => value !== null) ?? null;
      selectedCourierTipPercent = renderedCourierTipPercent;
    }
    synchronizeMethodSelection(selectedMethod);
    canonicalTextAttributes.forEach((value, name) => synchronizeTextAttribute(name, value));
    syncLocalDeliveryEligibility();
    forms.forEach(syncDiscountVerificationPrompt);
  };

  const scheduleRefresh = () => {
    if (refreshFrame !== null) return;
    refreshFrame = window.requestAnimationFrame(() => {
      refreshFrame = null;
      refresh();
    });
  };

  const schedulePersistence = (form) => {
    if (checkoutInProgress) return;
    window.clearTimeout(timers.get(form.id));
    timers.set(
      form.id,
      window.setTimeout(() => {
        timers.delete(form.id);
        if (checkoutInProgress) return;
        const updatedForm = currentForm(form.id);
        if (updatedForm) queuePersistence(updatedForm, buildAttributes);
      }, 200)
    );
  };

  const cancelAllPersistenceTimers = () => {
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.clear();
  };

  const setDiscountVerificationTerminalState = (form, status, message) => {
    discountVerification.revision += 1;
    discountVerification.status = status;
    discountVerification.challengeId = '';
    discountVerification.emailMasked = '';
    discountVerification.expiresAt = '';
    discountVerification.message = 'Email verification must be restarted to complete this order.';
    discountVerification.errorMessage = message;
    discountVerification.attempts = 0;
    discountVerification.proofToken = '';
    discountVerification.proofExpiresAt = '';
    discountVerification.binding = null;
    syncDiscountVerificationPrompt(form);
    const error = discountVerificationPrompt(form)?.querySelector(
      '[data-tp-discount-verification-error]'
    );
    if (typeof error?.focus === 'function') error.focus();
  };

  const verifyDiscountCode = async (form) => {
    if (discountVerification.status !== 'challenge') return;
    const prompt = discountVerificationPrompt(form);
    const input = prompt?.querySelector(discountVerificationCodeSelector);
    const code = String(input?.value || '').trim();
    if (!input || !/^\d{6}$/.test(code)) {
      const message = 'Enter the six-digit verification code.';
      if (input) {
        input.setCustomValidity(message);
        input.reportValidity();
      }
      discountVerification.errorMessage = message;
      syncDiscountVerificationPrompt(form);
      return;
    }
    if (expiryHasPassed(discountVerification.expiresAt)) {
      setDiscountVerificationTerminalState(
        form,
        'expired',
        'This code expired. Select Complete order to request a new code.'
      );
      return;
    }
    if (!discountVerificationBindingMatches(form)) {
      resetDiscountVerification();
      showPersistenceError(
        form,
        'Your checkout details changed. Complete your order again to request a new code.'
      );
      return;
    }
    if (discountVerification.attempts >= discountVerificationMaxAttempts) {
      setDiscountVerificationTerminalState(
        form,
        'locked',
        'This code can no longer be used. Select Complete order to request a new code.'
      );
      return;
    }

    const challengeId = discountVerification.challengeId;
    const revision = discountVerification.revision;
    discountVerification.attempts += 1;
    discountVerification.status = 'verifying';
    discountVerification.errorMessage = '';
    input.setCustomValidity('');
    syncDiscountVerificationPrompt(form);

    try {
      const response = await requestJson(
        '/apps/eden-tp/discount-verification/verify',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ challenge_id: challengeId, code }),
        },
        'Discount verification request'
      );
      const proofToken = safeSuggestedValue(response?.discount_verification_token, {
        maxLength: 512,
      });
      const proofExpiresAt = safeExpiry(response?.expires_at);
      if (response?.verified !== true || !proofToken || !proofExpiresAt) {
        throw new Error('TP returned an invalid discount verification confirmation.');
      }
      if (
        discountVerification.revision !== revision ||
        discountVerification.challengeId !== challengeId
      ) {
        return;
      }
      if (!discountVerificationBindingMatches(form)) {
        resetDiscountVerification();
        showPersistenceError(
          form,
          'Your checkout details changed. Complete your order again to request a new code.'
        );
        return;
      }
      discountVerification.status = 'verified';
      discountVerification.proofToken = proofToken;
      discountVerification.proofExpiresAt = proofExpiresAt;
      discountVerification.errorMessage = '';
      syncDiscountVerificationPrompt(form);
      await completeOrder(form);
    } catch (error) {
      if (
        discountVerification.revision !== revision ||
        discountVerification.challengeId !== challengeId
      ) {
        return;
      }
      const code = error?.validationDetail?.code;
      if (code === 'discount_verification_expired') {
        setDiscountVerificationTerminalState(
          form,
          'expired',
          'This code expired. Select Complete order to request a new code.'
        );
        return;
      }
      if (
        code === 'discount_verification_locked' ||
        discountVerification.attempts >= discountVerificationMaxAttempts
      ) {
        setDiscountVerificationTerminalState(
          form,
          'locked',
          'This code can no longer be used. Select Complete order to request a new code.'
        );
        return;
      }
      discountVerification.status = 'challenge';
      discountVerification.errorMessage = code === 'discount_verification_invalid'
        ? 'That code could not be verified. Check it and try again.'
        : 'We could not verify the code. Please try again.';
      syncDiscountVerificationPrompt(form);
      if (typeof input.focus === 'function') input.focus();
    }
  };

  const completeOrder = async (form, requestedSubmitter = null) => {
    if (pendingSubmissions.has(form)) return;
    const localDeliverySelected = selectedMethod === 'local_delivery';
    if (!localDeliverySelected && !validateDeliveryMethod(form)) return;
    if (form.dataset?.tpOrderSubmit === 'proxy') {
      if (['challenge', 'verifying'].includes(discountVerification.status)) {
        const prompt = discountVerificationPrompt(form);
        if (typeof prompt?.focus === 'function') prompt.focus();
        return;
      }
      if (['invalid', 'locked', 'expired'].includes(discountVerification.status)) {
        resetDiscountVerification();
      }
      const prompt = confirmationPrompt(form);
      if (prompt && !prompt.hidden) {
        if (typeof prompt.focus === 'function') prompt.focus();
        return;
      }
      if (!validateEmailConfirmation(form)) return;
      const state = confirmationState(form);
      if (
        selectedMethod === 'shipping' &&
        exhaustedUnverifiedAddressFingerprint === addressFingerprint(currentAddress(form))
      ) {
        showPersistenceError(
          form,
          'EasyPost still could not accept this shipping address. Edit it before trying again.'
        );
        const address1 = formControl(form, '[name="shipping_address[address1]"]');
        if (typeof address1?.focus === 'function') address1.focus();
        return;
      }
    }

    checkoutInProgress = true;
    cancelAllPersistenceTimers();
    pendingSubmissions.add(form);
    const submitter = requestedSubmitter || formControl(form, '[data-tp-complete-order]');
    if (submitter) submitter.disabled = true;

    try {
      if (localDeliverySelected) {
        const courierTipConsentBeforeRefresh = {
          enabled: courierTipCapabilityEnabled,
          percent:
            courierTipCapabilityEnabled &&
            courierTipPercentChoices.has(selectedCourierTipPercent)
              ? selectedCourierTipPercent
              : null,
        };
        const capabilityRefreshed = await loadDeliveryWindows({ force: true });
        const refreshedForm = currentForm(form.id) || form;
        if (!capabilityRefreshed || !validateDeliveryMethod(refreshedForm)) return;
        const courierTipConsentAfterRefresh = {
          enabled: courierTipCapabilityEnabled,
          percent:
            courierTipCapabilityEnabled &&
            courierTipPercentChoices.has(selectedCourierTipPercent)
              ? selectedCourierTipPercent
              : null,
        };
        if (
          courierTipConsentBeforeRefresh.enabled !==
            courierTipConsentAfterRefresh.enabled ||
          courierTipConsentBeforeRefresh.percent !==
            courierTipConsentAfterRefresh.percent
        ) {
          const errorTarget = showPersistenceError(
            refreshedForm,
            'Delivery tip options changed. Review the updated choice and select Complete order again.'
          );
          if (typeof errorTarget?.focus === 'function') errorTarget.focus();
          return;
        }
      }
      const result = await queuePersistence(form, buildAttributes);
      if (!result.ok || !syncLocalDeliveryEligibility()) return;

      const updatedForm = currentForm(form.id) || form;
      if (updatedForm.dataset?.tpOrderSubmit === 'cart') {
        window.location.assign(`${cartRoot()}cart?complete=1#CartCompleteOrder`);
        return;
      }
      if (updatedForm.dataset?.tpOrderSubmit !== 'proxy') {
        throw new Error('The order submission mode is invalid.');
      }

      const cart = await finalOrderCart();
      const expectedAttributes = buildAttributes(updatedForm);
      for (const [name, expectedValue] of Object.entries(expectedAttributes)) {
        if (String(cart.attributes[name] ?? '') !== String(expectedValue)) {
          throw new Error(`The final cart response did not confirm ${name}.`);
        }
      }
      setCartPricing(cart.total_price, cart.currency, { writeToForms: true });
      if (!syncLocalDeliveryEligibility()) return;
      await submitOrderToTp(updatedForm, cart);
    } catch (error) {
      if (handleStructuredValidationError(currentForm(form.id) || form, error)) return;
      const customerMessage =
        [409, 422].includes(error?.status) && error.userMessage
          ? error.userMessage
          : orderSubmissionErrorMessage;
      const errorTarget = showPersistenceError(
        currentForm(form.id) || form,
        customerMessage
      );
      if (typeof errorTarget?.focus === 'function') errorTarget.focus();
      console.error('Unable to complete order.', error);
    } finally {
      pendingSubmissions.delete(form);
      checkoutInProgress = false;
      if (submitter) submitter.disabled = false;
    }
  };

  document.addEventListener('click', async (event) => {
    if (!(event.target instanceof Element)) return;
    const verifiesDiscount = event.target.matches('[data-tp-verify-discount]');
    const acceptsSuggestion = event.target.matches('[data-tp-accept-suggestion]');
    const rejectsSuggestion = event.target.matches('[data-tp-reject-suggestion]');
    if (!verifiesDiscount && !acceptsSuggestion && !rejectsSuggestion) return;
    event.preventDefault();

    const form = [...document.querySelectorAll(formSelector)].find(
      (candidate) => candidate.dataset?.tpOrderSubmit === 'proxy'
    );
    if (verifiesDiscount) {
      if (form) await verifyDiscountCode(form);
      return;
    }
    if (!form || confirmationPrompt(form)?.hidden) return;
    const state = confirmationState(form);
    if (state.type === 'email_domain_suggestion') {
      const email = formControl(form, emailSelector);
      const emailConfirmation = formControl(form, emailConfirmationSelector);
      if (!email || !emailConfirmation) {
        clearConfirmationPrompt(form);
        showPersistenceError(form, orderSubmissionErrorMessage);
        return;
      }
      if (acceptsSuggestion) {
        email.value = state.suggestedEmail;
        emailConfirmation.value = state.suggestedEmail;
      }
      if (!validateEmailConfirmation(form)) return;
      state.emailDomainAcknowledged = true;
      state.acknowledgedEmail = String(email.value || '').trim();
      clearConfirmationPrompt(form);
      await completeOrder(form);
      return;
    }

    if (state.type === 'address_confirmation_required' && state.enteredAddress) {
      if (rejectsSuggestion) {
        clearConfirmationPrompt(form);
        showPersistenceError(form, 'Edit the delivery address, then complete your order again.');
        const address1 = formControl(form, '[name="shipping_address[address1]"]');
        if (typeof address1?.focus === 'function') address1.focus();
        return;
      }

      const pendingFingerprint = state.pendingUnverifiedAddressFingerprint;
      if (!pendingFingerprint || addressFingerprint(currentAddress(form)) !== pendingFingerprint) {
        clearConfirmationPrompt(form);
        showPersistenceError(form, 'The delivery address changed. Review it and complete your order again.');
        const address1 = formControl(form, '[name="shipping_address[address1]"]');
        if (typeof address1?.focus === 'function') address1.focus();
        return;
      }
      state.acknowledgedUnverifiedAddressFingerprint = pendingFingerprint;
      exhaustedUnverifiedAddressFingerprint = '';
      clearConfirmationPrompt(form);
      await completeOrder(form);
      return;
    }

    if (state.type !== 'address_correction_required' || !state.suggestedAddress) return;
    if (rejectsSuggestion) {
      clearConfirmationPrompt(form);
      showPersistenceError(form, 'Edit the delivery address, then complete your order again.');
      const address1 = formControl(form, '[name="shipping_address[address1]"]');
      if (typeof address1?.focus === 'function') address1.focus();
      return;
    }

    const address = state.suggestedAddress;
    try {
      writeSuggestedAddress(form, address);
    } catch (error) {
      clearConfirmationPrompt(form);
      showPersistenceError(form, orderSubmissionErrorMessage);
      console.error('Unable to apply the confirmed address.', error);
      return;
    }
    state.acknowledgedUnverifiedAddressFingerprint = '';
    exhaustedUnverifiedAddressFingerprint = '';
    state.acceptedAddressFingerprint = addressFingerprint(address);
    clearConfirmationPrompt(form);
    await completeOrder(form);
  });

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;
    const form = event.target.closest(formSelector) || (event.target.form?.matches(formSelector) ? event.target.form : null);
    if (form && event.target.matches(checkoutValidationSelector)) {
      handleCheckoutFieldEdit(form, event.target);
    }
    if (!event.target.matches(persistedFieldSelector)) return;
    if (!form || checkoutInProgress) return;

    if (event.target.matches(methodSelector)) {
      fulfillmentIntentRevision += 1;
      const requestedRevision = fulfillmentIntentRevision;
      synchronizeMethodSelection(event.target.checked ? event.target.value : '');
      syncLocalDeliveryEligibility();
      window.clearTimeout(timers.get(form.id));
      if (selectedMethod === 'local_delivery') {
        loadDeliveryWindows().then(() => {
          if (requestedRevision === fulfillmentIntentRevision && !checkoutInProgress) {
            queuePersistence(currentForm(form.id) || form, buildAttributes);
          }
        });
      } else {
        queuePersistence(form, buildAttributes);
      }
      return;
    }

    if (event.target.matches(shippingServiceSelector)) {
      fulfillmentIntentRevision += 1;
      selectedShippingService = event.target.checked && shippingServices.has(event.target.value)
        ? event.target.value
        : '';
      synchronizeMethodSelection(selectedMethod);
      window.clearTimeout(timers.get(form.id));
      queuePersistence(form, buildAttributes);
      return;
    }

    if (event.target.matches(courierTipSelector)) {
      fulfillmentIntentRevision += 1;
      selectedCourierTipPercent = event.target.checked
        ? parseCourierTipPercent(event.target.value)
        : null;
      syncCourierTipControls();
      window.clearTimeout(timers.get(form.id));
      queuePersistence(form, buildAttributes);
      return;
    }

    if (event.target.matches(deliveryDateSelector)) {
      fulfillmentIntentRevision += 1;
      requestedDeliveryDate = String(event.target.value || '').trim();
      requestedDeliveryWindow = '';
      syncDeliveryWindowControls();
      clearPersistenceError(form);
      schedulePersistence(form);
      return;
    }

    if (event.target.matches(deliveryWindowSelector)) {
      fulfillmentIntentRevision += 1;
      requestedDeliveryWindow = String(event.target.value || '').trim();
      syncDeliveryWindowControls();
      clearPersistenceError(form);
      schedulePersistence(form);
      return;
    }

    const name = textAttributeName(event.target);
    if (name) synchronizeTextAttribute(name, event.target.value || '');
    schedulePersistence(form);
  });

  document.addEventListener('input', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.matches(discountVerificationCodeSelector)) {
      event.target.setCustomValidity('');
      if (discountVerification.status === 'challenge') {
        discountVerification.errorMessage = '';
        const form = [...document.querySelectorAll(formSelector)].find(
          (candidate) => candidate.dataset?.tpOrderSubmit === 'proxy'
        );
        if (form) syncDiscountVerificationPrompt(form);
      }
      return;
    }
    const form = event.target.closest(formSelector) || (event.target.form?.matches(formSelector) ? event.target.form : null);
    if (form && event.target.matches(checkoutValidationSelector)) {
      handleCheckoutFieldEdit(form, event.target);
    }
    if (!event.target.matches(persistedFieldSelector)) return;
    if (
      event.target.matches(methodSelector) ||
      event.target.matches(shippingServiceSelector) ||
      event.target.matches(courierTipSelector) ||
      event.target.matches(deliveryDateSelector) ||
      event.target.matches(deliveryWindowSelector) ||
      checkoutInProgress
    ) return;
    if (!form) return;
    const name = textAttributeName(event.target);
    if (name) synchronizeTextAttribute(name, event.target.value || '');
    schedulePersistence(form);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(formSelector)) return;
    if (event.submitter && event.submitter.name !== 'tp_complete_order') return;

    event.preventDefault();
    await completeOrder(
      form,
      event.submitter || formControl(form, '[data-tp-complete-order]')
    );
  });

  document.addEventListener('cart:updated', () => {
    resetDiscountVerification();
    scheduleRefresh();
    if (!checkoutInProgress) refreshCartFromServer();
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    checkoutInProgress = false;
    cancelAllPersistenceTimers();
    document.querySelectorAll(formSelector).forEach((form) => {
      pendingSubmissions.delete(form);
    });
    refreshCartFromServer({ refreshCourierTipCapability: true });
  });
  new MutationObserver(scheduleRefresh).observe(document.body, { childList: true, subtree: true });

  window.EdenTpFulfillment = { refresh, refreshCart: refreshCartFromServer };
  refresh();
})();
